import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { clamp, debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest(
        'button, input, label, select, [tabindex="1"], vto-trigger, .card-vto'
      );
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    const activePreviewImage = cardGallery.querySelector(
      '[data-card-gallery-media]:not([hidden]) [ref="imagesToTransition[]"]'
    );

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) ||
      (activePreviewImage instanceof HTMLImageElement ? activePreviewImage : null) ||
      imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];
  #slideshowHoverSuppressed = false;
  #swatchThumbnailActive = false;
  /** @type {HTMLImageElement | null} */
  #activeSwatchThumb = null;
  /** @type {{ img: HTMLImageElement; src: string; srcset: string } | null} */
  #swatchPreviewSnapshot = null;
  #swatchThumbnailNavigationLock = false;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.#handleSlideshowArrowClick);
    this.addEventListener('click', this.navigateToProduct);
    this.addEventListener('pointerdown', this.#handleSwatchThumbnailPointerDown, { capture: true });
    this.addEventListener('touchend', this.#handleSwatchThumbnailTouch, { capture: true, passive: false });
    this.#updateCardGalleryProgress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleSlideshowArrowClick);
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('pointerdown', this.#handleSwatchThumbnailPointerDown, { capture: true });
    this.removeEventListener('touchend', this.#handleSwatchThumbnailTouch, { capture: true });
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.updateVariantDetails(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.updateCardGallery(event);
    this.#updateVariantImages();

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates variant details rendered inside the product card.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateVariantDetails(event) {
    const currentVariantDetails = this.querySelector('.product-variant-details');
    const newVariantDetails = event.detail.data.html.querySelector('.product-variant-details');

    if (currentVariantDetails && newVariantDetails) {
      morph(currentVariantDetails, newVariantDetails);
    } else if (currentVariantDetails && !newVariantDetails) {
      currentVariantDetails.remove();
    }
  }

  /**
   * Updates the card gallery markup rendered inside the product card.
   * Keeps the current root element so layout-specific classes and styles stay intact.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateCardGallery(event) {
    const currentCardGallery = this.refs.cardGallery;
    const currentImageRatio = currentCardGallery?.dataset.imageRatio;
    const newCardGallery =
      event.detail.data.html.querySelector(`[ref="cardGallery"][data-card-gallery-mode="${this.cardGalleryMode}"]`) ||
      event.detail.data.html.querySelector('[ref="cardGallery"]');

    if (!(currentCardGallery instanceof HTMLElement) || !(newCardGallery instanceof HTMLElement)) {
      return;
    }

    morph(currentCardGallery, newCardGallery);

    currentCardGallery.dataset.productId = newCardGallery.dataset.productId || '';
    currentCardGallery.dataset.cardGalleryMode = newCardGallery.dataset.cardGalleryMode || 'slideshow';

    if (newCardGallery.hasAttribute('data-view-transition-to-main-product')) {
      currentCardGallery.setAttribute('data-view-transition-to-main-product', '');
    } else {
      currentCardGallery.removeAttribute('data-view-transition-to-main-product');
    }

    if (currentImageRatio === 'adapt') {
      const nextAspectRatio = newCardGallery.style.getPropertyValue('--gallery-aspect-ratio').trim();

      if (nextAspectRatio) {
        currentCardGallery.style.setProperty('--gallery-aspect-ratio', nextAspectRatio);
      } else {
        currentCardGallery.style.removeProperty('--gallery-aspect-ratio');
      }
    }

    if (newCardGallery.hasAttribute('data-active-media-id')) {
      currentCardGallery.setAttribute('data-active-media-id', newCardGallery.getAttribute('data-active-media-id') || '');
    } else {
      currentCardGallery.removeAttribute('data-active-media-id');
    }

    if (newCardGallery.hasAttribute('data-default-media-id')) {
      currentCardGallery.setAttribute('data-default-media-id', newCardGallery.getAttribute('data-default-media-id') || '');
    } else {
      currentCardGallery.removeAttribute('data-default-media-id');
    }

    if (newCardGallery.hasAttribute('data-hover-media-id')) {
      currentCardGallery.setAttribute('data-hover-media-id', newCardGallery.getAttribute('data-hover-media-id') || '');
    } else {
      currentCardGallery.removeAttribute('data-hover-media-id');
    }

    if (newCardGallery.hasAttribute('on:pointerenter')) {
      currentCardGallery.setAttribute('on:pointerenter', newCardGallery.getAttribute('on:pointerenter') || '');
      currentCardGallery.setAttribute('on:pointerleave', newCardGallery.getAttribute('on:pointerleave') || '');
    } else {
      currentCardGallery.removeAttribute('on:pointerenter');
      currentCardGallery.removeAttribute('on:pointerleave');
    }

    if (this.cardGalleryMode === 'slideshow' && this.#slideshowHoverSuppressed) {
      this.#suppressSlideshowHoverMedia();
    }

    this.#updateCardGalleryProgress();
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    } else if (selectedImageId) {
      this.refs.cardGallery?.setAttribute('data-default-media-id', selectedImageId);
      this.#showPreviewGalleryMedia(selectedImageId);
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  get cardGalleryMode() {
    return this.refs.cardGallery?.dataset.cardGalleryMode || 'slideshow';
  }

  #getSlideshowHoverSlide() {
    const hoverSlide = this.refs.cardGallery?.querySelector('[data-card-gallery-hover-media]');
    return hoverSlide instanceof HTMLElement ? hoverSlide : null;
  }

  #getProgressSlides() {
    const allSlides = this.#getAllSlideshowSlides();
    const visibleProgressSlides = allSlides.filter(
      (slide) => !slide.hidden && !slide.hasAttribute('data-card-gallery-hover-media')
    );

    if (visibleProgressSlides.length) {
      return visibleProgressSlides;
    }

    const fallbackProgressSlides = allSlides.filter((slide) => !slide.hasAttribute('data-card-gallery-hover-media'));
    return fallbackProgressSlides.length ? fallbackProgressSlides : allSlides;
  }

  #getAllSlideshowSlides() {
    return Array.from(this.refs.slideshow?.refs?.slides || []);
  }

  #getActiveSlideshowSlide() {
    const { slideshow } = this.refs;
    const indexedSlide = slideshow?.slides?.[slideshow.current];
    if (indexedSlide instanceof HTMLElement) {
      return indexedSlide;
    }

    const allSlides = this.#getAllSlideshowSlides();
    const activeDomSlide = allSlides.find((slide) => slide.getAttribute('aria-hidden') === 'false');
    return activeDomSlide instanceof HTMLElement ? activeDomSlide : null;
  }

  #getProgressSlideIndex(progressSlides, activeSlide) {
    if (!progressSlides.length || !(activeSlide instanceof HTMLElement)) {
      return 0;
    }

    const directIndex = progressSlides.indexOf(activeSlide);
    if (directIndex >= 0) {
      return directIndex;
    }

    const allSlides = this.#getAllSlideshowSlides();
    const activeIndex = allSlides.indexOf(activeSlide);
    if (activeIndex < 0) {
      return 0;
    }

    for (let index = activeIndex - 1; index >= 0; index -= 1) {
      const slide = allSlides[index];
      const progressIndex = progressSlides.indexOf(slide);

      if (progressIndex >= 0) {
        return progressIndex;
      }
    }

    for (let index = activeIndex + 1; index < allSlides.length; index += 1) {
      const slide = allSlides[index];
      const progressIndex = progressSlides.indexOf(slide);

      if (progressIndex >= 0) {
        return progressIndex;
      }
    }

    return 0;
  }

  #suppressSlideshowHoverMedia(direction = 'next') {
    const { slideshow } = this.refs;
    const hoverSlide = this.#getSlideshowHoverSlide();

    if (!slideshow || !hoverSlide) {
      return;
    }

    const currentSlide = slideshow.slides?.[slideshow.current];
    const allSlides = slideshow.refs.slides || [];
    const currentIndex = currentSlide ? allSlides.indexOf(currentSlide) : -1;
    const wasHoverSlideActive = currentSlide === hoverSlide;

    hoverSlide.hidden = true;
    hoverSlide.removeAttribute('reveal');
    hoverSlide.setAttribute('aria-hidden', 'true');

    if (!wasHoverSlideActive) {
      this.#updateCardGalleryProgress();
      return;
    }

    const searchDirections = direction === 'previous' ? [-1, 1] : [1, -1];
    let targetSlide = null;

    for (const step of searchDirections) {
      for (let index = currentIndex + step; index >= 0 && index < allSlides.length; index += step) {
        const slide = allSlides[index];

        if (slide.hidden || slide.hasAttribute('data-card-gallery-hover-media')) {
          continue;
        }

        targetSlide = slide;
        break;
      }

      if (targetSlide) {
        break;
      }
    }

    const targetSlideId = targetSlide?.getAttribute('slide-id');
    if (targetSlideId) {
      slideshow.select({ id: targetSlideId }, undefined, { animate: false });
      return;
    }

    this.#updateCardGalleryProgress();
  }

  #getPreviewGalleryMedia() {
    return Array.from(this.refs.cardGallery?.querySelectorAll('[data-card-gallery-media]') || []);
  }

  #showPreviewGalleryMedia(mediaId) {
    const previewMedia = this.#getPreviewGalleryMedia();
    if (!previewMedia.length) return;

    let didMatch = false;

    for (const media of previewMedia) {
      if (!(media instanceof HTMLElement)) continue;

      const isActive = media.dataset.mediaId === String(mediaId);
      media.hidden = !isActive;
      didMatch ||= isActive;
    }

    if (didMatch) {
      this.refs.cardGallery?.setAttribute('data-active-media-id', String(mediaId));
      this.#updateCardGalleryProgress();
    }
  }

  #updateCardGalleryProgress() {
    if (this.refs.slideshow) {
      this.#updateSlideshowGalleryProgress();
      return;
    }

    this.#updatePreviewGalleryProgress();
  }

  #updateSlideshowGalleryProgress() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const progressSlides = this.#getProgressSlides();
    if (!progressSlides.length) return;

    const activeSlide = this.#getActiveSlideshowSlide();
    const currentIndex = this.#getProgressSlideIndex(progressSlides, activeSlide) + 1;

    this.#renderCardGalleryProgress(currentIndex, progressSlides.length);
  }

  #updatePreviewGalleryProgress() {
    const previewMedia = this.#getPreviewGalleryMedia();
    if (!previewMedia.length) return;

    const activeIndex = Math.max(previewMedia.findIndex((media) => media.hidden === false), 0);

    this.#renderCardGalleryProgress(activeIndex + 1, previewMedia.length);
  }

  #renderCardGalleryProgress(current, total) {
    const progressBar = this.refs.cardGallery?.querySelector('[ref="cardGalleryProgressbar"]');

    if (!(progressBar instanceof HTMLElement)) {
      return;
    }

    const safeTotal = Math.max(total, 1);
    const safeCurrent = clamp(current, 1, safeTotal);

    progressBar.style.setProperty('--progress-width', `${(safeCurrent / safeTotal) * 100}%`);
  }

  #getPreviewGalleryDefaultMediaId() {
    const selectedOptionMediaId = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    if (selectedOptionMediaId) {
      return selectedOptionMediaId;
    }

    const configuredDefaultMediaId = this.refs.cardGallery?.dataset.defaultMediaId;
    if (configuredDefaultMediaId) {
      return configuredDefaultMediaId;
    }

    const defaultPreviewMedia = this.#getPreviewGalleryMedia().find((media) => media.hasAttribute('data-default-media'));
    return defaultPreviewMedia instanceof HTMLElement ? defaultPreviewMedia.dataset.mediaId : null;
  }

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    this.#updateSlideshowGalleryProgress();
  };

  #handleSlideshowArrowClick = (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const arrowControl = event.target.closest('slideshow-arrows .slideshow-control');
    if (!(arrowControl instanceof HTMLButtonElement)) {
      return;
    }

    if (!this.#getSlideshowHoverSlide()) {
      return;
    }

    this.#slideshowHoverSuppressed = true;

    const direction = arrowControl.classList.contains('slideshow-control--previous') ? 'previous' : 'next';
    queueMicrotask(() => {
      this.#suppressSlideshowHoverMedia(direction);
    });
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) {
      this.#showPreviewGalleryMedia(id);
      return;
    }

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the hover media for preview-mode card galleries.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (this.#swatchThumbnailActive) return;

    const hoverMediaId = this.refs.cardGallery?.dataset.hoverMediaId;
    if (!hoverMediaId || this.#slideshowHoverSuppressed) {
      return;
    }

    if (this.refs.slideshow) {
      this.refs.slideshow.select({ id: hoverMediaId }, undefined, { animate: false });
      return;
    }

    if (hoverMediaId) {
      this.#showPreviewGalleryMedia(hoverMediaId);
    }
  }

  /**
   * Resets the image to the variant image.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (this.refs.slideshow && this.#slideshowHoverSuppressed) return;

    if (this.refs.slideshow) {
      this.#resetVariant();
      return;
    }

    if (!this.variantPicker) {
      const defaultMediaId = this.#getPreviewGalleryDefaultMediaId();
      if (defaultMediaId) {
        this.#showPreviewGalleryMedia(defaultMediaId);
      }
      return;
    }

    this.#resetVariant();
  }

  /**
   * Previews the variant image when hovering a PLP thumbnail swatch.
   * @param {PointerEvent} event
   */
  previewSwatchThumbnail(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;

    const thumb = event.target instanceof Element ? event.target.closest('.product-card-swatches-preview__thumb') : null;
    if (!(thumb instanceof HTMLImageElement) || thumb === this.#activeSwatchThumb) return;

    this.#swatchThumbnailActive = true;
    this.#setActiveSwatchThumb(thumb);

    const mediaId = thumb.dataset.swatchMediaId;
    const imageSrc = this.#normalizeImageSrc(thumb.dataset.swatchImageSrc);

    if (mediaId && this.refs.slideshow) {
      const matchingSlide = this.refs.slideshow.slides?.find((slide) => slide.getAttribute('slide-id') === mediaId);
      if (matchingSlide) {
        this.#exitSwatchSwapPreview();
        this.refs.slideshow.select({ id: mediaId }, undefined, { animate: false });
        return;
      }

      this.#selectDefaultSlideshowSlide();

      const activeImage = this.#getActiveGalleryImage();
      const needsRebind =
        !this.#swatchPreviewSnapshot ||
        !(activeImage instanceof HTMLImageElement) ||
        this.#swatchPreviewSnapshot.img !== activeImage;

      this.#swapActiveGalleryImage(imageSrc, { rebind: needsRebind });
      return;
    } else if (mediaId && this.refs.cardGallery?.classList.contains('card-gallery--preview')) {
      this.#showPreviewGalleryMedia(mediaId);
      return;
    }

    this.#swapActiveGalleryImage(imageSrc, { rebind: true });
  }

  /**
   * Restores the default gallery image after leaving PLP thumbnail swatches.
   * @param {PointerEvent} event
   */
  resetSwatchThumbnail(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (this.#swatchThumbnailNavigationLock) return;

    const preview =
      event.target instanceof Element ? event.target.closest('.product-card-swatches-preview--thumbnails') : null;
    if (!(preview instanceof Element)) return;

    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && preview.contains(nextTarget)) return;

    this.#swatchThumbnailActive = false;
    this.#setActiveSwatchThumb(null);
    this.#clearSwatchThumbnailPreview();
  }

  #handleSwatchThumbnailPointerDown = (event) => {
    const thumbLink =
      event.target instanceof Element ? event.target.closest('.product-card-swatches-preview__thumb-link') : null;
    if (thumbLink instanceof HTMLAnchorElement) {
      this.#swatchThumbnailNavigationLock = true;
    }
  };

  /**
   * Navigates to the PDP for the variant represented by a PLP thumbnail.
   * Stops the gallery overlay link from handling the click.
   * @param {PointerEvent} event
   */
  navigateFromSwatchThumbnail(event) {
    const thumbLink =
      event.target instanceof Element ? event.target.closest('.product-card-swatches-preview__thumb-link') : null;
    if (!(thumbLink instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.#swatchThumbnailNavigationLock = true;
    this.#navigateToSwatchVariant(thumbLink, event);
  }

  #handleSwatchThumbnailTouch = (event) => {
    if (event.changedTouches.length !== 1) return;

    const thumbLink =
      event.target instanceof Element ? event.target.closest('.product-card-swatches-preview__thumb-link') : null;
    if (!(thumbLink instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.#navigateToSwatchVariant(thumbLink, event)) {
      this.#swatchThumbnailNavigationLock = true;
    }
  };

  /**
   * @param {HTMLAnchorElement} thumbLink
   * @param {Event} event
   */
  #navigateToSwatchVariant(thumbLink, event) {
    const variantId = thumbLink.dataset.variantId;
    if (!variantId) return false;

    const url = new URL(thumbLink.href || this.refs.productCardLink.href, window.location.origin);
    url.searchParams.set('variant', variantId);
    this.#navigateToURL(event, url);
    return true;
  }

  #exitSwatchSwapPreview() {
    this.#restoreActiveGalleryImage();
  }

  #clearSwatchThumbnailPreview() {
    this.#restoreActiveGalleryImage();
    this.#resetVariant();
  }

  #selectDefaultSlideshowSlide() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    const firstSlideId = slideshow.slides?.[0]?.getAttribute('slide-id');
    if (firstSlideId) {
      slideshow.select({ id: firstSlideId }, undefined, { animate: false });
    }
  }

  /**
   * @param {string | undefined} src
   */
  #normalizeImageSrc(src) {
    if (!src) return src;
    return src.startsWith('//') ? `https:${src}` : src;
  }

  #setActiveSwatchThumb(thumb) {
    this.#activeSwatchThumb = thumb;
  }

  #getActiveGalleryImage() {
    const { cardGallery, slideshow } = this.refs;
    if (!cardGallery) return null;

    if (slideshow) {
      const currentSlide = slideshow.slides?.[slideshow.current];
      const slideImage = currentSlide?.querySelector('img');
      if (slideImage instanceof HTMLImageElement) return slideImage;
    }

    const activeImage = cardGallery.querySelector('[data-card-gallery-media]:not([hidden]) img');

    return activeImage instanceof HTMLImageElement ? activeImage : null;
  }

  /**
   * @param {string | undefined} src
   * @param {{ rebind?: boolean }} [options]
   */
  #swapActiveGalleryImage(src, { rebind = false } = {}) {
    const normalizedSrc = this.#normalizeImageSrc(src);
    if (!normalizedSrc) return;

    if (rebind && this.#swatchPreviewSnapshot) {
      this.#restoreActiveGalleryImage();
    }

    if (!this.#swatchPreviewSnapshot || rebind) {
      const image = this.#getActiveGalleryImage();
      if (!image) return;

      this.#swatchPreviewSnapshot = {
        img: image,
        src: image.currentSrc || image.src,
        srcset: image.srcset,
      };
    }

    const { img } = this.#swatchPreviewSnapshot;
    img.src = normalizedSrc;
    img.removeAttribute('srcset');
  }

  #restoreActiveGalleryImage() {
    if (!this.#swatchPreviewSnapshot) return;

    const { img, src, srcset } = this.#swatchPreviewSnapshot;
    img.src = src;
    if (srcset) {
      img.srcset = srcset;
    } else {
      img.removeAttribute('srcset');
    }

    this.#swatchPreviewSnapshot = null;
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) {
      const defaultMediaId = this.#getPreviewGalleryDefaultMediaId();
      if (defaultMediaId) {
        this.#showPreviewGalleryMedia(defaultMediaId);
      }
      return;
    }

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    if (this.#swatchThumbnailNavigationLock) return;

    const interactiveElement = event.target.closest(
      'button, input, label, select, [tabindex="1"], .product-card-swatches-preview__thumb-link, .product-card-swatches-preview__thumb, .product-card-swatches-preview, vto-trigger, .card-vto'
    );

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', this.getSectionRenderingId());

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
